import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Link, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { Link as RouterLink, useOutletContext, useParams } from 'react-router-dom';
import {
  getQrCodes,
  portalErrorMessage,
  reportPortalActivity
} from '../../features/portal/portalService';
import PrintableQRCodes from '../ui/grcode/printableQRCodes';
import {
  clubPortalBase,
  clubRinkQrPrintPath,
  clubSectionQrPrintPath,
  portalUrl,
  secretaryPortalBase,
  secretaryRinkQrPrintPath,
  secretarySectionQrPrintPath
} from './portalPaths';
import {
  PortalEmpty,
  PortalError,
  PortalLoading,
  PortalPageHeader
} from './portalCommon';

const PortalQrPrint = ({ sheet, mode }) => {
  const { eventId, venueId } = useParams();
  const { token } = useOutletContext();
  const isRinks = sheet === 'rinks';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);

  const load = useCallback(() => {
    if (!token || !eventId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');

    getQrCodes(token)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((requestError) => {
        if (!cancelled) setError(portalErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, eventId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load, retry]);

  const eventName = data?.event?.name || '';
  const scopeName = data?.scope?.venueName || '';
  const title = isRinks ? 'Rink QR codes' : 'Section QR codes';
  const subtitle = [scopeName, eventName].filter(Boolean).join(' • ');

  const groups = useMemo(() => {
    if (!data) return [];

    if (!isRinks) {
      const sections = Array.isArray(data.sections) ? data.sections : [];
      if (sections.length === 0) return [];

      return [
        {
          key: 'sections',
          heading: '',
          items: sections.map((section) => ({
            key: section.id,
            label: section.name,
            value: portalUrl(section.path)
          }))
        }
      ];
    }

    return (Array.isArray(data.venues) ? data.venues : [])
      .map((venue) => ({
        key: venue.venueId || venue.venueName,
        heading: venue.venueName,
        items: (Array.isArray(venue.greens) ? venue.greens : []).flatMap((green) =>
          (Array.isArray(green.rinks) ? green.rinks : []).map((rink) => ({
            key: `${venue.venueId}:${green.name}:${rink.rink}`,
            label: rink.label,
            caption: venue.venueName,
            value: portalUrl(rink.path)
          }))
        )
      }))
      .filter((group) => group.items.length > 0);
  }, [data, isRinks]);

  const itemCount = groups.reduce((total, group) => total + group.items.length, 0);
  const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
  const visibleWarnings = warnings.slice(0, 5);
  const isSecretary = mode === 'secretary';

  const backTo = isSecretary
    ? secretaryPortalBase(eventId)
    : clubPortalBase(eventId, venueId);

  const handlePrint = () => {
    if (itemCount > 0) {
      const label = isRinks ? 'Rink QR codes' : 'Section QR codes';
      const kind = isRinks ? 'rink' : 'section';
      reportPortalActivity(
        token,
        {
          operation: 'qr.print',
          entityId: eventId,
          entityName: label,
          summary: `${itemCount} ${kind} QR code${
            itemCount === 1 ? '' : 's'
          }${scopeName ? ` · ${scopeName}` : ''}`,
          metadata: {
            sheet: isRinks ? 'rinks' : 'sections',
            itemCount,
            portalKind: mode,
            venueName: scopeName || null
          }
        },
        { eventId }
      );
    }

    window.print();
  };

  let otherSheetTo = '';
  let otherSheetLabel = '';
  let emptyMessage = '';

  if (isRinks) {
    otherSheetTo = isSecretary
      ? secretarySectionQrPrintPath(eventId)
      : clubSectionQrPrintPath(eventId, venueId);
    otherSheetLabel = 'Open section QR codes';
    emptyMessage = isSecretary
      ? 'No scheduled games are assigned to rinks for this event yet.'
      : 'No scheduled games are assigned to rinks for this club yet.';
  } else {
    otherSheetTo = isSecretary
      ? secretaryRinkQrPrintPath(eventId)
      : clubRinkQrPrintPath(eventId, venueId);
    otherSheetLabel = 'Open rink QR codes';
    emptyMessage = isSecretary
      ? 'No sections are set up for this event.'
      : 'There are no sections with games for this club yet.';
  }

  return (
    <Box>
      <PortalPageHeader
        title={title}
        subtitle={subtitle}
        backTo={backTo}
        backLabel={isSecretary ? 'Clubs' : 'Sections'}
        action={
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={loading || itemCount === 0}>
            Print codes
          </Button>
        }
      />

      {loading && <PortalLoading />}
      {!loading && error && <PortalError message={error} onRetry={() => setRetry((n) => n + 1)} />}

      {!loading && !error && data && warnings.length > 0 && (
        <Alert severity="warning" className="no-print" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Some codes were skipped
          </Typography>
          {visibleWarnings.map((warning, index) => (
            <Typography
              key={`${warning.type}-${warning.label}-${index}`}
              variant="body2">
              {[warning.venueName, warning.label, warning.message]
                .filter(Boolean)
                .join(' — ')}
            </Typography>
          ))}
          {warnings.length > visibleWarnings.length && (
            <Typography variant="body2">
              +{warnings.length - visibleWarnings.length} more
            </Typography>
          )}
        </Alert>
      )}

      {!loading && !error && data && itemCount === 0 && (
        <PortalEmpty>
          {emptyMessage}{' '}
          <Link component={RouterLink} to={otherSheetTo}>
            {otherSheetLabel}
          </Link>
          .
        </PortalEmpty>
      )}

      {!loading && !error && data && !isRinks && mode === 'club' && itemCount > 0 && (
        <Alert severity="info" className="no-print" sx={{ mb: 2 }}>
          These codes open the whole section, including games from other clubs.
        </Alert>
      )}

      {!loading && !error && data && itemCount > 0 && (
        <PrintableQRCodes
          groups={groups}
          linkLabel={isRinks ? 'View Game' : 'View Section'}
          eventName={eventName}
        />
      )}
    </Box>
  );
};

export default PortalQrPrint;
